import styled from 'styled-components';
import theme from 'styled-theming';
import { MarketPositionNewContainer } from '../MarketPositionNew/style';

const rowBackground = theme('name', { light: '#f5f5f5', dark: '#2a3037' });
const expandedBackground = theme('name', { light: '#f9f9fb', dark: '#2a3037' });
const rowBorder = theme('name', { light: '#e5e5e5', dark: '#454b52' });
const treeColumnWidth = 232;
const totalColumnWidth = 90;

export const BalancesContainer = styled(MarketPositionNewContainer)<{
  $columnCount: number;
  $columnWidth: number;
}>`
  .filter-row {
    gap: 8px;
    margin-bottom: 18px;
  }

  .filter-label {
    line-height: 16px;
  }

  .period-gas-day-field {
    width: 340px;
    max-width: 100%;
  }

  .period-gas-day-controls {
    display: flex;
    align-items: center;
  }

  .period-filter-field {
    flex: 0 0 130px;
    margin-right: 0;
  }

  .gas-day-controls {
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 0;

    > button {
      flex: 0 0 24px;
      width: 24px;
      min-width: 24px;
      padding: 0;
    }
  }

  .gas-day-picker {
    flex: 1;
    min-width: 0;
  }

  .gas-day {
    width: 100%;
  }

  .search-container,
  .search-filter-field,
  .custom-view-filter-field {
    margin-left: 0;
  }

  .search-container {
    width: 200px;
    min-width: 200px;
  }

  .search-container:has(.search-clear-btn) {
    width: 260px;
    min-width: 260px;
  }

  .legal-entity-filter-field {
    width: 360px;
  }

  .custom-view-filter-field {
    padding-left: 0;
  }

  .custom-view-controls .shell-select {
    width: 200px;
    flex-basis: 200px;
  }

  .market-button-group {
    margin-bottom: 24px;
  }

  .market-button {
    border-radius: 6px;
    height: 30px;
    line-height: 18px;
  }

  .table-area {
    margin: 0 8px;
  }

  .time-series-wrapper {
    border: 0;
    border-radius: 0;
  }

  .balances-viewport {
    /* stylelint-disable-next-line property-no-unknown -- Container queries postdate Stylelint 13. */
    container-type: inline-size;
    width: 100%;
  }

  .filter-row button,
  .filter-row .shell-select-selector,
  .filter-row .shell-text-input,
  .filter-row .shell-date-picker {
    min-height: 30px;
    height: 30px;
  }

  .filter-row .legal-entity-filter-field {
    .shell-select-selector {
      height: auto;
    }

    .shell-select-selection-item {
      max-width: 100%;
      min-width: 0;
    }

    .shell-select-selection-item-content {
      min-width: 0;
      white-space: nowrap;
    }
  }

  .balances-table {
    table-layout: fixed;
    width: ${({ $columnCount, $columnWidth }) =>
      treeColumnWidth + totalColumnWidth + $columnCount * $columnWidth}px;
    min-width: 0;

    th,
    td {
      width: ${({ $columnWidth }) => $columnWidth}px;
      min-width: ${({ $columnWidth }) => $columnWidth}px;
      height: 36px;
      padding: 4px 8px;
      border-right: 0;
      border-bottom: 0;
      line-height: 20px;
      text-align: center;
    }

    .tree-column {
      width: ${treeColumnWidth}px;
      min-width: ${treeColumnWidth}px;
      max-width: ${treeColumnWidth}px;
    }

    .total-column {
      position: sticky;
      left: ${treeColumnWidth}px;
      z-index: 4;
      width: ${totalColumnWidth}px;
      min-width: ${totalColumnWidth}px;
      max-width: ${totalColumnWidth}px;
      box-shadow: 1px 0 0 ${rowBorder};
    }

    thead .total-column {
      z-index: 5;
    }

    tbody .total-column {
      font-weight: 400;
    }

    thead th:nth-child(n) {
      background: ${(props) => props.theme.background.surface};
      border-bottom: 1px solid ${(props) => props.theme.border.medium};
    }

    thead th:not(.tree-column)::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      height: 12px;
      border-left: 1px solid ${rowBorder};
    }

    .balance-market-row td {
      background: ${rowBackground};
      border-top: 8px solid ${(props) => props.theme.background.surface};
      text-align: left;
      height: 44px;
    }

    .market-summary-cell {
      overflow: visible;
      padding: 0;
    }

    .market-summary-content {
      position: sticky;
      left: 0;
      z-index: 2;
      /* stylelint-disable-next-line unit-no-unknown -- Container units postdate Stylelint 13. */
      width: 100cqi;
      min-height: 36px;
      box-sizing: border-box;
      padding: 0 12px;
      background: inherit;
    }

    .balance-market-header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px 8px;
    }

    .balance-empty-cell {
      white-space: normal;
      text-align: left;
    }

    .balance-market-toggle,
    .balance-toggle {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      border: 0;
      padding: 4px 0;
      background: none;
      color: inherit;
      font: inherit;
      font-size: 14px;
      cursor: pointer;

      > svg {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
      }

      > svg:first-child {
        width: 12px;
        height: 12px;
      }
    }

    .balance-market-toggle {
      gap: 12px;
    }

    .balance-tag {
      border: 1px solid ${(props) => props.theme.text.medium};
      border-radius: 3px;
      background: transparent;
      color: inherit;
      padding: 0 6px;
      font: inherit;
      font-size: 11px;
      line-height: 16px;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;

      &[aria-pressed='true'] {
        border-color: #0085a8;
        box-shadow: inset 0 0 0 1px #0085a8;
      }
    }

    .balance-row td {
      background: ${expandedBackground};
    }

    .balance-toggle {
      padding-left: 20px;
    }

    .balance-badge,
    .position-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 14px;
      height: 14px;
      border-radius: 50%;
      background: #f58b00;
      color: #fff;
      font-size: 10px;
      line-height: 14px;
    }

    .balance-position-label {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0 0 0 54px;
      overflow: hidden;
    }

    .position-badge {
      background: #6c96c8;
      color: #fff;
    }

    .balance-position-row td {
      border-bottom: 1px solid ${rowBorder};
    }

    .balance-position-row:nth-child(even) td {
      background: ${rowBackground};
    }

    .balance-market-row:has(.balance-market-toggle[aria-expanded='true']) td {
      background: ${expandedBackground};
    }

    .position-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 700;
    }

    button:focus-visible {
      outline: 2px solid #0085a8;
      outline-offset: 2px;
    }
  }

  .balance-error {
    padding: 16px;
    color: ${(props) => props.theme.text.error};
  }
`;
